import { MemoryRouter } from "react-router-dom";
import { useT, Namespace } from "@racoon/i18n";
import { useListContainerGroupsQuery } from "~/shared/api/container-group/container-group.grpc";
import { DataTable, Hyperlink, type Column } from "@racoon/ui";
import { ContractStatus } from "@racoon/api";
import { format, parseISO } from "date-fns";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";

interface ObjectsTabProps {
  receiverOrgId?: number;
}

export const ObjectsTab: React.FC<ObjectsTabProps> = ({ receiverOrgId }) => {
  const t = useT();

  const { data, isLoading } = useListContainerGroupsQuery(
    { ownerOrganizationIds: receiverOrgId ? [BigInt(receiverOrgId)] : [] },
    { skip: !receiverOrgId },
  );

  // The rows only carry the location's id and name, so the card fetches the
  // full location on click.
  const { openLocationById, pickupLocationCard } = usePickupLocationCard();

  const objects = data?.containerGroups || [];

  const columns: Column<(typeof objects)[0]>[] = [
    {
      id: "name",
      header: t(Namespace.Customers, "card.objects.column.name"),
      cell: (info: any) => {
        const row = info?.row?.original || info;
        const name = row.pickupLocationName || row.pickup_location_name;
        const locationId = row.pickupLocationId || row.pickup_location_id;
        if (!name) return "—";
        if (!locationId) return name;
        return <Hyperlink onOpen={() => openLocationById(locationId)}>{name}</Hyperlink>;
      },
      align: "left",
    },

    {
      id: "containerGroup",
      header: t(Namespace.Customers, "card.objects.column.containerGroup").toUpperCase(),
      accessor: (row: any) => {
        const typeName =
          row.containerType?.name ||
          row.containerType?.typeName ||
          row.container_type?.type_name ||
          "—";
        const count = row.numberOfContainers || row.number_of_containers || 0;
        return `${typeName} (${count})`;
      },
      align: "left",
    },

    {
      id: "contractNumber",
      header: t(Namespace.Customers, "card.objects.column.contractNumber").toUpperCase(),
      accessor: (row: any) => row.contract_id || row.contractId || "",
      cell: (info: any) => {
        const row = info?.row?.original || info;
        const contract = row?.contract;

        if (!contract || contract.status === ContractStatus.CONTRACT_STATUS_HIDDEN) {
          return t(Namespace.Customers, "card.objects.noContract");
        }

        const dealNum = contract.dealNumber || contract.deal_number || "—";
        const contractName = contract.name || "";
        const startDateString = contract.startAt || contract.start_at;

        let dateStr = "";
        if (startDateString) {
          try {
            let dateObj: Date;

            if (typeof startDateString === "string") {
              dateObj = parseISO(startDateString);
            } else if (typeof startDateString === "object") {
              if ("seconds" in startDateString) {
                dateObj = new Date(Number(startDateString.seconds) * 1000);
              } else if (typeof startDateString.toDate === "function") {
                dateObj = startDateString.toDate();
              } else {
                dateObj = new Date(startDateString as any);
              }
            } else if (typeof startDateString === "number") {
              dateObj = new Date(startDateString);
            } else {
              dateObj = new Date(startDateString as any);
            }

            if (!isNaN(dateObj.getTime())) {
              dateStr = format(dateObj, "dd.MM.yyyy");
            }
          } catch {}
        }
        const parts = [`№${dealNum}`];

        if (contractName) {
          parts.push(contractName);
        }

        if (dateStr) {
          parts.push(`от ${dateStr}`);
        }

        return parts.join(" ");
      },
      align: "left",
    },
  ];

  if (!receiverOrgId) {
    return (
      <div className="flex justify-center items-center h-32">
        <span className="text-gray-400">{t(Namespace.Customers, "card.objects.noData")}</span>
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={objects}
        loading={isLoading}
        rowKey={(row: any) => row.id}
        empty={
          <div className="flex justify-center items-center h-30">
            <span className="text-gray-400">{t(Namespace.Customers, "card.objects.empty")}</span>
          </div>
        }
      />
      {/* MemoryRouter because the card links internally and this tab is itself
          inside a modal. */}
      <MemoryRouter>{pickupLocationCard}</MemoryRouter>
    </>
  );
};
